<template lang="html">
  <div class="blog-feed flex min-h-full grow flex-col bg-elevated">
    <div class="mx-auto w-full max-w-(--ui-container) px-4 py-5 sm:px-6 lg:py-6">
      <ContentBreadcrumbs
        class="mb-3"
        :items="breadcrumbItems"
      />

      <div class="mb-4 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h1 class="text-2xl font-bold tracking-tight text-highlighted sm:text-3xl">
          Блог
        </h1>
        <span
          v-if="resultCountLabel"
          class="text-sm font-medium text-muted sm:text-base"
        >
          {{ resultCountLabel }}
        </span>
      </div>

      <div
        class="sticky top-(--ui-header-height) z-30 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-default bg-default px-3 py-2 shadow-xs"
      >
        <ContentTagChips
          class="min-w-0 flex-1"
          base-path="/blog"
          :tags="availableTags"
          :active-tag="tag"
        />

        <USelectMenu
          v-model="selectedSort"
          :items="sortOptions"
          value-key="value"
          :search-input="false"
          variant="none"
          class="ml-auto shrink-0 cursor-pointer text-sm text-muted hover:text-default"
          :ui="{ content: 'min-w-52' }"
        />
      </div>

      <ContentFeedList
        :items="collection"
        :pending="pending"
        base-path="/blog"
        empty-text="Статей пока нет."
        :next-href="nextHref"
        @load-more="loadMore"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BreadcrumbItem } from '@nuxt/ui'
import { ContentType, SortDirection } from '@repo/api/web/models'

import ContentBreadcrumbs from '../../components/Listing/ContentBreadcrumbs.vue'
import ContentFeedList from '../../components/Listing/ContentFeedList.vue'
import ContentTagChips from '../../components/Listing/ContentTagChips.vue'

definePageMeta({
  name: 'BlogIndex',
  layoutContainer: 'full',
})

const {
  collection,
  pending,
  nextHref,
  tag,
  loadMore,
  applySort,
  availableTags,
  hasAfter,
} = await useContentFeed({
  type: ContentType.ARTICLE,
  listKey: 'blog-list',
})

useContentListSeo({ feedTitle: 'Блог', descriptionBase: 'Статьи блога', tag })

const sortOptions = [
  {
    label: 'Сначала новые',
    value: 'publishAt:desc',
  },
  {
    label: 'Сначала старые',
    value: 'publishAt:asc',
  },
]

const selectedSort = ref(sortOptions[0]!.value)

watch(selectedSort, (value) => {
  const [field, dir] = value.split(':')
  applySort(
    field || 'publishAt',
    dir === 'asc' ? SortDirection.ASC : SortDirection.DESC,
  )
})

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = [
    { label: 'Главная', to: '/' },
    { label: 'Блог', to: tag.value ? '/blog' : undefined },
  ]
  if (tag.value) {
    items.push({ label: tag.value })
  }
  return items
})

function pluralizeArticle(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return 'статья'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'статьи'
  return 'статей'
}

const resultCountLabel = computed(() => {
  const n = collection.value.length
  if (!n && pending.value) return ''
  const suffix = hasAfter.value ? '+' : ''
  return `${n}${suffix} ${pluralizeArticle(n || 0)}`
})
</script>
